import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Play, Square, Check, ChevronDown, ChevronsRight, X } from 'lucide-react'
import {
  SidePanel,
  SidePanelContent,
  SidePanelBody,
} from '@/components/application/side-panel'
import { Button } from '@/components/base/buttons'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner } from '@/components/common'
import { useVoices } from '@/hooks/api'
import { playGreetingAudio } from '@/hooks/api/useGreetings'
import { getLanguageFlag, getLanguageDisplayName } from '@/lib/languageFlags'
import { cn } from '@/lib/utils'
import type { Voice } from '@/types/assistant'

interface VoiceSelectionDrawerProps {
  isOpen: boolean
  onClose: () => void
  selectedVoiceId?: number | null
  onSelect: (voice: Voice) => void
  greeting?: string
  defaultLanguage?: string
  assistantId?: number
}

// Format meta values to labels
function formatMetaLabel(value: string | undefined): string {
  if (!value) return ''
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Filter dropdown component
interface FilterDropdownProps {
  label: string
  value: string | null
  options: string[]
  onChange: (value: string | null) => void
  formatOption?: (value: string) => string
  allLabel: string
}

function FilterDropdown({
  label,
  value,
  options,
  onChange,
  formatOption = formatMetaLabel,
  allLabel,
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors',
          value
            ? 'border-primary-300 bg-primary-50 font-medium text-primary-700 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
        )}
      >
        {value ? formatOption(value) : label}
        <ChevronDown
          className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          {/* Dropdown menu */}
          <div className="absolute left-0 top-full z-20 mt-1 min-w-[160px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <button
              type="button"
              onClick={() => {
                onChange(null)
                setIsOpen(false)
              }}
              className={cn(
                'flex w-full items-center px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700',
                !value && 'bg-gray-50 font-medium dark:bg-gray-700'
              )}
            >
              {allLabel}
            </button>
            {options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onChange(option)
                  setIsOpen(false)
                }}
                className={cn(
                  'flex w-full items-center px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700',
                  value === option && 'bg-gray-50 font-medium dark:bg-gray-700'
                )}
              >
                {formatOption(option)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// Voice card component
interface VoiceCardProps {
  voice: Voice
  isSelected: boolean
  isPlaying: boolean
  onPlay: (e: React.MouseEvent) => void
  onSelect: () => void
  translations: {
    playPreview: string
    playingPreview: string
    voiceLabel: string
    availableInPlan: string
    premiumPlan: string
    selected: string
    select: string
  }
}

function VoiceCard({
  voice,
  isSelected,
  isPlaying,
  onPlay,
  onSelect,
  translations,
}: VoiceCardProps) {
  const { i18n } = useTranslation()
  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-lg border p-4 transition-all',
        isSelected
          ? 'border-primary-300 bg-primary-50/50 dark:border-primary-700 dark:bg-primary-900/20'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      )}
    >
      {/* Top row: Play + Name + Tags + Plan badge */}
      <div className="flex items-start gap-4">
        {/* Play button */}
        <button
          type="button"
          onClick={onPlay}
          aria-label={isPlaying ? translations.playingPreview : translations.playPreview}
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition-colors',
            'border-2 border-gray-300 bg-white text-gray-600',
            'hover:border-primary-500 hover:text-primary-600',
            'dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300',
            isPlaying && 'border-primary-500 text-primary-600'
          )}
        >
          {isPlaying ? (
            <Square className="h-4 w-4 fill-current" />
          ) : (
            <Play className="ml-0.5 h-5 w-5" />
          )}
        </button>

        {/* Voice info */}
        <div className="flex flex-1 flex-col gap-2">
          <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {voice.display_name}
          </span>

          {/* Meta tags */}
          <div className="flex flex-wrap gap-2">
            {voice.meta?.age_group && (
              <Badge color="secondary" size="sm">
                {formatMetaLabel(voice.meta.age_group)}
              </Badge>
            )}
            {voice.meta?.gender && (
              <Badge color="secondary" size="sm">
                {translations.voiceLabel.replace('{{gender}}', formatMetaLabel(voice.meta.gender))}
              </Badge>
            )}
            {voice.meta?.tones?.slice(0, 2).map((tone) => (
              <Badge key={tone} color="secondary" size="sm">
                {formatMetaLabel(tone)}
              </Badge>
            ))}
          </div>
        </div>

        {/* Plan badge */}
        <div className="shrink-0">
          {voice.is_public ? (
            <Badge color="secondary" className="whitespace-nowrap">
              <Check className="mr-1 h-3 w-3" />
              {translations.availableInPlan}
            </Badge>
          ) : (
            <Badge
              color="secondary"
              className="whitespace-nowrap bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
            >
              {translations.premiumPlan}
            </Badge>
          )}
        </div>
      </div>

      {/* Description */}
      {voice.description && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {voice.description}
        </p>
      )}

      {/* Bottom row: Languages + Select button */}
      <div className="flex items-center gap-4">
        {/* Languages */}
        {voice.meta?.languages && voice.meta.languages.length > 0 && (
          <div className="flex flex-1 items-center gap-2">
            <div className="flex gap-1">
              {voice.meta.languages.slice(0, 2).map((lang) => (
                <span key={lang} className="text-lg">
                  {getLanguageFlag(lang)}
                </span>
              ))}
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {voice.meta.languages
                .slice(0, 2)
                .map((lang) => getLanguageDisplayName(lang, i18n.language)?.split(' ')[0])
                .join(', ')}
              {voice.meta.languages.length > 2 && (
                <span className="text-gray-400">
                  {' '}
                  +{voice.meta.languages.length - 2}
                </span>
              )}
            </span>
          </div>
        )}

        {/* Select button */}
        <Button
          type="button"
          color={isSelected ? 'secondary' : 'primary'}
          onPress={onSelect}
          isDisabled={!voice.is_public && !voice.available_in_plan}
          className="ml-auto"
          iconLeading={isSelected ? Check : undefined}
        >
          {isSelected ? translations.selected : translations.select}
        </Button>
      </div>
    </div>
  )
}

export function VoiceSelectionDrawer({
  isOpen,
  onClose,
  selectedVoiceId,
  onSelect,
  greeting = 'Hello, how can I help you today?',
  defaultLanguage = 'en-US',
  assistantId,
}: VoiceSelectionDrawerProps) {
  const { t, i18n } = useTranslation()
  const [playingVoiceId, setPlayingVoiceId] = useState<number | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const cleanupAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.removeAttribute('src')
      audioRef.current = null
    }
    setPlayingVoiceId(null)
  }, [])

  useEffect(() => () => cleanupAudio(), [cleanupAudio])

  const [filters, setFilters] = useState({
    age_group: null as string | null,
    gender: null as string | null,
    tone: null as string | null,
    language: null as string | null,
  })
  const [sortBy, setSortBy] = useState<'suggested' | 'name_asc' | 'name_desc'>('suggested')

  // Fetch voices
  const { data: voices = [], isLoading } = useVoices({
    enabled: isOpen,
  })

  // Get unique filter options from voices
  const filterOptions = useMemo(() => {
    const ageGroups = new Set<string>()
    const genders = new Set<string>()
    const tones = new Set<string>()
    const languages = new Set<string>()

    voices.forEach((voice) => {
      if (voice.meta?.age_group) ageGroups.add(voice.meta.age_group)
      if (voice.meta?.gender) genders.add(voice.meta.gender)
      voice.meta?.tones?.forEach((t) => tones.add(t))
      voice.meta?.languages?.forEach((l) => languages.add(l))
    })

    return {
      age_group: Array.from(ageGroups).sort(),
      gender: Array.from(genders).sort(),
      tone: Array.from(tones).sort(),
      language: Array.from(languages).sort(),
    }
  }, [voices])

  // Filter and sort voices
  const filteredVoices = useMemo(() => {
    const filtered = voices.filter((voice) => {
      if (filters.age_group && voice.meta?.age_group !== filters.age_group) {
        return false
      }
      if (filters.gender && voice.meta?.gender !== filters.gender) {
        return false
      }
      if (
        filters.tone &&
        !voice.meta?.tones?.includes(filters.tone)
      ) {
        return false
      }
      if (
        filters.language &&
        !voice.meta?.languages?.includes(filters.language)
      ) {
        return false
      }
      return true
    })

    // Sort voices
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'suggested':
          // Sort by suggested_weight descending (highest first)
          const weightA = a.meta?.suggested_weight ?? 0
          const weightB = b.meta?.suggested_weight ?? 0
          return weightB - weightA
        case 'name_asc':
          return (a.display_name || '').localeCompare(b.display_name || '')
        case 'name_desc':
          return (b.display_name || '').localeCompare(a.display_name || '')
        default:
          return 0
      }
    })
  }, [voices, filters, sortBy])

  // Check if any filters are active (sort is not considered a filter)
  const hasActiveFilters =
    filters.age_group || filters.gender || filters.tone || filters.language

  const resetFilters = () => {
    setFilters({
      age_group: null,
      gender: null,
      tone: null,
      language: null,
    })
    setSortBy('suggested')
  }

  const handlePlayPreview = async (voice: Voice, e: React.MouseEvent) => {
    e.stopPropagation()

    if (playingVoiceId === voice.id) {
      cleanupAudio()
      return
    }

    // Stop any currently playing audio before starting a new one
    cleanupAudio()

    try {
      setPlayingVoiceId(voice.id)
      const audio = await playGreetingAudio({
        text: greeting,
        language: defaultLanguage,
        id: assistantId || 0,
        voice_id: voice.id,
      })
      audioRef.current = audio
      audio.onended = () => {
        audioRef.current = null
        setPlayingVoiceId(null)
      }
    } catch (error) {
      console.error('Failed to play audio preview:', error)
      setPlayingVoiceId(null)
    }
  }

  const handleSelect = (voice: Voice) => {
    onSelect(voice)
    onClose()
  }

  // Translation strings for VoiceCard
  const voiceCardTranslations = {
    playPreview: t('resources.assistants.voice_drawer.play_preview'),
    playingPreview: t('resources.assistants.voice_drawer.playing_preview'),
    voiceLabel: t('resources.assistants.voice_drawer.voice_label'),
    availableInPlan: t('resources.assistants.voice_drawer.available_in_plan'),
    premiumPlan: t('resources.assistants.voice_drawer.premium_plan'),
    selected: t('resources.assistants.voice_drawer.selected'),
    select: t('resources.assistants.voice_drawer.select'),
  }

  const allLabel = t('resources.assistants.voice_drawer.filters.all')

  return (
    <SidePanel
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      onClose={onClose}
      side="right"
      size="full"
      variant="default"
    >
      <SidePanelContent>
        {/* Custom header with double chevron close button */}
        <div className="flex flex-col gap-4 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            aria-label={t('resources.assistants.voice_drawer.close')}
          >
            <ChevronsRight className="h-5 w-5" />
          </button>

          {/* Title and subtitle */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {t('resources.assistants.voice_drawer.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.voice_drawer.subtitle')}
            </p>
          </div>

          {/* Filter buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <FilterDropdown
              label={t('resources.assistants.voice_drawer.filters.age_group')}
              value={filters.age_group}
              options={filterOptions.age_group}
              onChange={(v) => setFilters((f) => ({ ...f, age_group: v }))}
              allLabel={allLabel}
            />
            <FilterDropdown
              label={t('resources.assistants.voice_drawer.filters.gender')}
              value={filters.gender}
              options={filterOptions.gender}
              onChange={(v) => setFilters((f) => ({ ...f, gender: v }))}
              allLabel={allLabel}
            />
            <FilterDropdown
              label={t('resources.assistants.voice_drawer.filters.tone')}
              value={filters.tone}
              options={filterOptions.tone}
              onChange={(v) => setFilters((f) => ({ ...f, tone: v }))}
              allLabel={allLabel}
            />
            <FilterDropdown
              label={t('resources.assistants.voice_drawer.filters.language')}
              value={filters.language}
              options={filterOptions.language}
              onChange={(v) => setFilters((f) => ({ ...f, language: v }))}
              formatOption={(lang) => getLanguageDisplayName(lang, i18n.language) || lang}
              allLabel={allLabel}
            />

            {/* Spacer */}
            <div className="flex-1" />

            {/* Sort dropdown */}
            <FilterDropdown
              label={t('resources.assistants.voice_drawer.filters.popularity', { defaultValue: 'Popularity' })}
              value={sortBy === 'suggested' ? null : sortBy}
              options={['name_asc', 'name_desc']}
              onChange={(v) => setSortBy((v as typeof sortBy) || 'suggested')}
              formatOption={(value) => {
                switch (value) {
                  case 'name_asc':
                    return t('resources.assistants.voice_drawer.filters.name_asc', { defaultValue: 'Name A-Z' })
                  case 'name_desc':
                    return t('resources.assistants.voice_drawer.filters.name_desc', { defaultValue: 'Name Z-A' })
                  default:
                    return value
                }
              }}
              allLabel={t('resources.assistants.voice_drawer.filters.popularity', { defaultValue: 'Popularity' })}
            />

            {/* Reset filters button */}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <X className="h-4 w-4" />
                {t('resources.assistants.voice_drawer.filters.reset')}
              </button>
            )}
          </div>
        </div>

        {/* Voice cards */}
        <SidePanelBody>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : filteredVoices.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              {t('resources.assistants.voice_drawer.no_voices_found')}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {filteredVoices.map((voice) => (
                <VoiceCard
                  key={voice.id}
                  voice={voice}
                  isSelected={voice.id === selectedVoiceId}
                  isPlaying={playingVoiceId === voice.id}
                  onPlay={(e) => handlePlayPreview(voice, e)}
                  onSelect={() => handleSelect(voice)}
                  translations={voiceCardTranslations}
                />
              ))}
            </div>
          )}
        </SidePanelBody>
      </SidePanelContent>
    </SidePanel>
  )
}

export default VoiceSelectionDrawer
